import type { BffResponse } from './types/bff';
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager';
import { FeatureAppDefinition, FeatureAppEnvironment, FeatureServices, Logger } from '@feature-hub/core';
import { HistoryServiceV2 } from '@feature-hub/history-service';
import { ReactFeatureApp } from '@feature-hub/react';
import { SerializedStateManagerV1 } from '@feature-hub/serialized-state-manager';
import { ServerRequestV1 } from '@feature-hub/server-request';
import { ApiKeyProviderV1 } from '@volkswagen-onehub/api-key-provider';
import { AuthServiceProviderV2, IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { BreadcrumbServiceV1 } from '@volkswagen-onehub/breadcrumb-service';
import { ThemeProvider } from '@volkswagen-onehub/components-core';
import { DisclaimerManagerV1 } from '@volkswagen-onehub/disclaimer-manager';
import { FaContentServiceV1 } from '@volkswagen-onehub/fa-content-service';
import { FeatureAppMemoryStoreV1 } from '@volkswagen-onehub/feature-app-memory-store';
import { LayerManagerV24 } from '@volkswagen-onehub/layer-manager';
import { LocaleServiceV1 } from '@volkswagen-onehub/locale-service';
import { NavigationServiceV1 } from '@volkswagen-onehub/navigation-service';
import { PageInfoServiceV1 } from '@volkswagen-onehub/page-info-service';
import { ServiceConfigProviderV1 } from '@volkswagen-onehub/service-config-provider';
import { TrackingManagerV1 } from '@volkswagen-onehub/tracking-service';
import { UniversalState } from '@volkswagen-onehub/vw-starter-feature-app-support';
import { UserInteractionServiceV22 } from '@volkswagen-onehub/user-interaction-service';
import { ProfileServiceV2Impl, UserService } from '@vwdfive/myvw-services';
import React from 'react';
import { App } from './app';
import { IPortalContext, PortalContext } from './context/PortalContext';
import { Environment, EnvironmentEnum } from './enviroment';
import { PortalServiceV1 } from '@esportal/portal-service';
import { FeatureappServiceV1 } from '@volkswagen-onehub/featureapp-service';

export interface EsportalFeatureAppDependencies extends FeatureServices {
	readonly 's2:logger'?: Logger;
	readonly 's2:history': HistoryServiceV2;
	readonly 'es:portal-feature-service': PortalServiceV1;
	readonly 'vw:authService': AuthServiceProviderV2;
	readonly 'layer-manager': LayerManagerV24;
	readonly 'fa-content-service'?: FaContentServiceV1;
	readonly 'tracking': TrackingManagerV1;
	readonly 'navigation-service': NavigationServiceV1;
	readonly 'api-key-provider'?: ApiKeyProviderV1;
	readonly 'disclaimer-manager': DisclaimerManagerV1;
	readonly 'locale-service': LocaleServiceV1;
	readonly 's2:server-request'?: ServerRequestV1;
	readonly 'service-config-provider': ServiceConfigProviderV1;
	readonly 'page-info-service': PageInfoServiceV1;
	readonly 'breadcrumb-service': BreadcrumbServiceV1;
	readonly 's2:serialized-state-manager': SerializedStateManagerV1;
	readonly 's2:async-ssr-manager': AsyncSsrManagerV1;
	readonly 'user-interaction-service'?: UserInteractionServiceV22;
	readonly 'feature-app-memory-store': FeatureAppMemoryStoreV1<UniversalState<BffResponse>>;
}

declare global {
	var s: { persCmp?: string };
}

export const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, EsportalFeatureAppDependencies> = {
	dependencies: {
		featureServices: {
			['vw:authService']: '2.0.0',
			's2:history': '^2.0.0',
			'tracking': '^1.0.0',
			'navigation-service': '^1.0.0',
			'es:portal-feature-service': '^1.0.0',
			'service-config-provider': '1.0.0',
			'fa-content-service': '^1.0.0',
			'locale-service': '^1.0.0',
			'disclaimer-manager': '^1.1.0',
			'feature-app-memory-store': '^1.0.0',
			'user-interaction-service': '^2.2.0',
			'layer-manager': '^2.5.0',
			's2:serialized-state-manager': '^1.0.0',
			'featureapp-service': '^1.0.0',
		},
		externals: {
			'react': '^18.3.1',
			'@feature-hub/react': '^3.4.0',
		},
	},

	optionalDependencies: {
		featureServices: {
			['vw:authService']: '2.0.0',
			'page-info-service': '^1.0.0',
			'breadcrumb-service': '^1.0.0',
			'api-key-provider': '^1.0.0',
			'fa-content-service': '^1.0.0',
			's2:async-ssr-manager': '^1.0.0',
			's2:logger': '^1.0.0',
			's2:server-request': '^1.0.0',
			'tracking': '^1.0.0',
		},
	},

	//ownFeatureServiceDefinitions: [portalFeatureServiceDefinition],

	create: (env: FeatureAppEnvironment<FeatureServices, unknown>) => {
		console.log('🚀 ~ env', env);

		const featureAppId = env.featureAppId;
		const featureServices = env.featureServices;

		if (env.config) {
			Environment.getInstance((env.config as { environment: EnvironmentEnum }).environment);
		}

		const serviceConfigProvider = env.featureServices['service-config-provider'] as ServiceConfigProviderV1;

		const authServiceBuilder = env.featureServices['vw:authService'] as AuthServiceProviderV2;

		const authServiceMarketFlag: IAuthServiceV2 = authServiceBuilder.register('market');

		const profileService = new ProfileServiceV2Impl(
			authServiceMarketFlag,
			serviceConfigProvider.configs['customer-profile']!.urlOrigin,
		);
		const userService = new UserService(authServiceMarketFlag);
		const portalService = env.featureServices['es:portal-feature-service'] as PortalServiceV1;
		const layerManager = env.featureServices['layer-manager'] as LayerManagerV24;
		const contentService = env.featureServices['fa-content-service'] as FaContentServiceV1;
		const historyService = env.featureServices['s2:history'] as HistoryServiceV2;
		const pageInfoService = env.featureServices['page-info-service'] as PageInfoServiceV1;
		const disclaimerManager = env.featureServices['disclaimer-manager'] as DisclaimerManagerV1;
		const breacrumbService = env.featureServices['breadcrumb-service'] as BreadcrumbServiceV1;
		const navigationService = env.featureServices['navigation-service'] as NavigationServiceV1;
		const featureAppService = env.featureServices['featureapp-service'] as FeatureappServiceV1;
		const userInteractionService = featureServices['user-interaction-service'] as UserInteractionServiceV22;
		const trackingService = env.featureServices['tracking'];

		/*
    // initialize the mock server and mock ids
    initializeFetchJson('esportal', logger, config);

    // retrieve serialized state on client and store it on server
    manageBffSerializationAndCaching(
      serializedStateManager,
      memoryStore,
      logger,
    );

    // assemble common bff options
    // TODO extract to support
    const commonBffFetchOptions = createBffFetchOptions(
      baseUrl,
      config || {},
      featureServices,
    );

    const loadStatus = memoryStore.getState()?.status || 'loading';

    const bffLoadComplete = loadStatus === 'loaded' || loadStatus === 'error';

    const bffPromise = bffLoadComplete
      ? undefined
      : fetchBffData('/esportal', commonBffFetchOptions).then((response) => {
          if (response) {
            saveBffResponse(memoryStore, response);
          }

          return undefined;
        });

    // TODO necessary for SSR support. Remove otherwise.
    if (serverRequest && bffPromise) {
      asyncSsrManager?.scheduleRerender(bffPromise);
    }

    const featureAppContent = contentService?.content;
		*/
		return {
			//loadingPromise: serverRequest || bffLoadComplete ? undefined : bffPromise,

			// eslint-disable-next-line react/display-name
			render: () => {
				/*
        const state = memoryStore.getState();
        if (state?.status !== 'loaded') {
          return null;
        }*/
				return (
					<PortalContext.Provider
						value={
							{
								featureAppId,
								authService: authServiceMarketFlag,
								profileService,
								layerManager,
								userService,
								portalService,
								serviceConfigProvider,
								featureServices,
								historyService,
								disclaimerManager,
								breacrumbService,
								pageInfoService,
								navigationService,
								featureAppService,
								contentService,
								trackingService,
								userInteractionService,
							} as unknown as IPortalContext
						}
					>
						<ThemeProvider theme="main">
							<App />
						</ThemeProvider>
					</PortalContext.Provider>
				);
			},
		};
	},
};

export default featureAppDefinition;
